import { Dispatch, MutableRefObject, SetStateAction, useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { useMutation } from 'react-relay';
import { SitesQuery$data } from '../../graphQLOperations/__generated__/SitesQuery.graphql';
import { Toast } from 'primereact/toast';
import { sitesUpdateSitesTargetGroups } from '../../graphQLOperations/Sites';
import { SitesUpdateSiteTargetGroupsMutation } from '../../graphQLOperations/__generated__/SitesUpdateSiteTargetGroupsMutation.graphql';
import PanelMenuRoadmaps from './PanelMenuRoadmaps';
import { groupTargetGroupsByStandardGrade } from '../../businessLogicService/getTargetGroupByStandardGrade';
import { TargetGroup } from '../SkillsTable/SkillsTable';
import { sortByKey } from '../../businessLogicService/sortCriteria';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';

interface Props {
  data: SitesQuery$data | null;
  toast: MutableRefObject<Toast | null>;
  setFetchKey: Dispatch<SetStateAction<number>>;
  setRefetchingData: Dispatch<SetStateAction<boolean>>;
}
export interface SelectedRoadmap {
  siteId: string;
  targetGroup: {
    id: string;
    name: string;
  }[];
}

const ROWS = 5;
const SettingsTabRoadmaps = ({ data, toast, setFetchKey, setRefetchingData }: Props) => {
  const [first, setFirst] = useState<number>(0);
  const [selectedRoadmaps, setSelectedRoadmaps] = useState<SelectedRoadmap[]>([]);
  const [addSitesTarget, loading] = useMutation<SitesUpdateSiteTargetGroupsMutation>(sitesUpdateSitesTargetGroups);

  const onSubmit = () => {
    addSitesTarget({
      variables: {
        input: selectedRoadmaps.map(el => ({
          siteId: el.siteId,
          targetGroupIds: el.targetGroup.map(e => {
            return e.id;
          }),
        })),
      },
      onCompleted: () => {
        toast?.current?.show({
          severity: 'success',
          summary: 'Roadmaps Updated',
          detail: 'The Roadmaps available to your site(s) have been updated',
        });
        setFetchKey(new Date().getTime());
        setRefetchingData(true);
      },
      onError: () => {
        setSelectedRoadmaps(prev => prev);
        toast?.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Something went wrong. Please try again',
        });
      },
    });
  };

  const onPageChange = (event: PaginatorPageChangeEvent) => {
    setFirst(event.first);
  };

  useEffect(() => {
    const matchRoadmaps: SelectedRoadmap[] =
      data?.sites?.nodes?.map(site => {
        const curriculumTargetGroupIds = new Set<string>();

        site?.siteStandardStateCurriculums?.forEach(ssc => {
          ssc?.standardStateCurriculum?.curriculum?.targetGroups?.forEach(tg => {
            if (tg?.id) curriculumTargetGroupIds.add(tg.id);
          });
        });

        const matchingTargetGroups =
          site?.targetGroups?.filter(tg => curriculumTargetGroupIds.has(tg?.id as string)) || [];

        return {
          siteId: site?.id ?? '',
          targetGroup: (matchingTargetGroups as { id: string; name: string }[]) ?? [],
        };
      }) ?? [];

    setSelectedRoadmaps(matchRoadmaps);
  }, [data]);

  return (
    <>
      <div className="flex flex-column nc_content-frame" id="roadmapsSettings">
        <div>
          <h3>
            This tab allows you to set which <em>Roadmaps</em> are available at the site(s)
          </h3>
          <ul>
            <li>
              Students will be able to choose any of the <span className="p-highlight p-1">highlighted</span>{' '}
              <em>Roadmaps</em> for the site to work towards. Teachers and Admins will be able to use any of these{' '}
              <em>Roadmaps</em> to plan or monitor student progress.
            </li>
            <li>
              Adding or removing available <em>Roadmaps</em> will not impact skill completion data for students.
            </li>
            <li>Be sure to scroll to the bottom and click the Save button!</li>
          </ul>
        </div>
        <div className="flex flex-wrap gap-xl">
          {data?.sites?.nodes?.slice(first, first + ROWS)?.map((site, key) => {
            const targetGroups = site?.siteStandardStateCurriculums?.flatMap(
              ssc =>
                ssc?.standardStateCurriculum?.curriculum?.targetGroups?.map(tg => ({
                  id: tg?.id ?? '',
                  name: tg?.name ?? '',
                  standardGrade: { id: tg?.standardGrade?.id ?? '', name: tg?.standardGrade?.name ?? '' },
                })) ?? [],
            );

            let availableTargetGroup = groupTargetGroupsByStandardGrade(targetGroups as TargetGroup[] | []);
            availableTargetGroup = [...availableTargetGroup].sort(sortByKey('name'));

            return (
              <PanelMenuRoadmaps
                key={key}
                site={{
                  id: site?.id ?? '',
                  name: site?.name ?? '',
                  availableTargetGroupGroupedByStandardGrade: availableTargetGroup.map(grade => ({
                    id: grade.id,
                    name: grade.name,
                    targetGroup:
                      grade.targetGroups?.map(tg => ({
                        id: tg.id,
                        name: tg.name,
                      })) ?? [],
                  })),
                }}
                selectedRoadmap={selectedRoadmaps?.find(s => s?.siteId === site?.id) ?? { siteId: '', targetGroup: [] }}
                setSelectedRoadmaps={setSelectedRoadmaps}
              />
            );
          })}
        </div>
        <div className="text-right">
          <Button
            loading={loading}
            disabled={!selectedRoadmaps?.every(t => t?.targetGroup?.length > 0) || loading}
            label="Save"
            icon="nc_icon-nc_save icon-sm"
            aria-label="Save settings"
            rounded
            onClick={onSubmit}
            id="roadmapsSaveButton"
          />
        </div>
        <Paginator
          className="nc_roadmap-tab-paginator"
          first={first}
          rows={ROWS}
          totalRecords={data?.sites?.nodes?.length}
          onPageChange={onPageChange}
        />
      </div>
    </>
  );
};

export default SettingsTabRoadmaps;
